import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import './LeadSourceSelector.css';

function LeadSourceSelector({ selectedSources, onSourcesChange }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [currentSource, setCurrentSource] = useState(null);
    const [tempStrength, setTempStrength] = useState(5);
    const [tempPostingFrequency, setTempPostingFrequency] = useState(20);
    const [tempContentQuality, setTempContentQuality] = useState(5);
    const [tempFollowerCount, setTempFollowerCount] = useState('');
    const [tempNotes, setTempNotes] = useState('');

    // Available Lead Sources
    const availableLeadSources = [
        // Organic
        { id: 'personal', label: 'Persönliches Netzwerk', icon: 'fas fa-user-friends', category: 'organic' },
        { id: 'organic-instagram', label: 'Organisch Instagram', icon: 'fab fa-instagram', category: 'organic' },
        { id: 'organic-tiktok', label: 'Organisch TikTok', icon: 'fab fa-tiktok', category: 'organic' },
        { id: 'organic-youtube', label: 'Organisch YouTube', icon: 'fab fa-youtube', category: 'organic' },
        { id: 'organic-facebook', label: 'Organisch Facebook', icon: 'fab fa-facebook', category: 'organic' },
        { id: 'organic-linkedin', label: 'Organisch LinkedIn', icon: 'fab fa-linkedin', category: 'organic' },
        { id: 'seo', label: 'SEO / Google Search', icon: 'fas fa-search', category: 'organic' },
        { id: 'podcast', label: 'Podcast', icon: 'fas fa-podcast', category: 'organic' },
        { id: 'content-marketing', label: 'Content Marketing', icon: 'fas fa-file-alt', category: 'organic' },
        { id: 'referrals', label: 'Empfehlungen', icon: 'fas fa-handshake', category: 'organic' },
        { id: 'community', label: 'Community', icon: 'fas fa-users', category: 'organic' },
        // Paid
        { id: 'meta-ads', label: 'Meta Ads (FB & IG)', icon: 'fab fa-facebook-square', category: 'paid' },
        { id: 'google-ads', label: 'Google Ads', icon: 'fab fa-google', category: 'paid' },
        { id: 'youtube-ads', label: 'YouTube Ads', icon: 'fab fa-youtube', category: 'paid' },
        { id: 'tiktok-ads', label: 'TikTok Ads', icon: 'fab fa-tiktok', category: 'paid' },
        { id: 'linkedin-ads', label: 'LinkedIn Ads', icon: 'fab fa-linkedin', category: 'paid' },
        { id: 'pinterest-ads', label: 'Pinterest Ads', icon: 'fab fa-pinterest', category: 'paid' },
        { id: 'twitter-ads', label: 'Twitter Ads', icon: 'fab fa-twitter', category: 'paid' },
        { id: 'spotify-ads', label: 'Spotify Ads', icon: 'fab fa-spotify', category: 'paid' },
        // Other
        { id: 'cold-email', label: 'Cold Email', icon: 'fas fa-envelope', category: 'other' },
        { id: 'cold-calling', label: 'Cold Calling', icon: 'fas fa-phone', category: 'other' },
        { id: 'live-events', label: 'Live Events', icon: 'fas fa-calendar-check', category: 'other' },
        { id: 'webinars', label: 'Webinare', icon: 'fas fa-video', category: 'other' },
        { id: 'partnerships', label: 'Partnerschaften', icon: 'fas fa-handshake', category: 'other' },
        { id: 'affiliate', label: 'Affiliate Marketing', icon: 'fas fa-link', category: 'other' },
        { id: 'newsletter', label: 'Newsletter', icon: 'fas fa-paper-plane', category: 'other' }
    ];

    const isSourceSelected = (sourceId) => {
        return selectedSources.some(s => s.source === sourceId);
    };

    const getSourceStrength = (sourceId) => {
        const source = selectedSources.find(s => s.source === sourceId);
        return source?.strength || 5;
    };

    const handleSourceClick = (sourceId) => {
        const existing = selectedSources.find(s => s.source === sourceId);
        
        if (existing) {
            // Already selected - open modal to edit
            setCurrentSource(sourceId);
            setTempStrength(existing.strength);
            setTempPostingFrequency(existing.postingFrequency || 20);
            setTempContentQuality(existing.contentQuality || 5);
            setTempFollowerCount(existing.followerCount || '');
            setTempNotes(existing.notes || '');
            setModalOpen(true);
        } else {
            // New source - open modal to set
            setCurrentSource(sourceId);
            setTempStrength(5);
            setTempPostingFrequency(20);
            setTempContentQuality(5);
            setTempFollowerCount('');
            setTempNotes('');
            setModalOpen(true);
        }
    };

    const isOrganicPlatform = (sourceId) => {
        return ['organic-instagram', 'organic-tiktok', 'organic-youtube', 'organic-facebook', 'organic-linkedin'].includes(sourceId);
    };

    const handleSaveStrength = () => {
        const sourceConfig = availableLeadSources.find(s => s.id === currentSource);
        const existingIndex = selectedSources.findIndex(s => s.source === currentSource);
        
        const sourceData = {
            strength: tempStrength,
            notes: tempNotes,
            ...(isOrganicPlatform(currentSource) && {
                postingFrequency: tempPostingFrequency,
                contentQuality: tempContentQuality,
                followerCount: tempFollowerCount
            })
        };
        
        let newSources;
        if (existingIndex >= 0) {
            // Update existing
            newSources = [...selectedSources];
            newSources[existingIndex] = { ...newSources[existingIndex], ...sourceData };
        } else {
            // Add new
            newSources = [...selectedSources, {
                id: Date.now(),
                source: currentSource,
                notes: '',
                ...sourceData
            }];
        }
        
        onSourcesChange(newSources);
        setModalOpen(false);
        setCurrentSource(null);
    };

    const handleRemoveSource = (e, sourceId) => {
        e.stopPropagation();
        const newSources = selectedSources.filter(s => s.source !== sourceId);
        onSourcesChange(newSources);
    };

    const getStrengthLabel = (strength) => {
        if (strength <= 3) return '🔵 Wenig Traffic';
        if (strength <= 5) return '📊 Gelegentlich';
        if (strength <= 7) return '✅ Regelmäßig';
        if (strength <= 9) return '🔥 Wichtig';
        return '⭐ Hauptkanal';
    };

    return (
        <div className="lead-source-selector">
            {/* Selected Sources Summary */}
            {selectedSources.length > 0 && (
                <div className="lead-source-selected-summary">
                    <h4>
                        <i className="fas fa-check-circle"></i>
                        Ausgewählte Quellen ({selectedSources.length})
                    </h4>
                    <div className="lead-source-selected-badges">
                        {selectedSources.map(source => {
                            const config = availableLeadSources.find(s => s.id === source.source);
                            return (
                                <div key={source.id} className="lead-source-selected-badge">
                                    <i className={config?.icon}></i>
                                    <span>{config?.label}</span>
                                    <span className="lead-source-strength-indicator">{source.strength}/10</span>
                                    <button
                                        className="lead-source-badge-edit"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSourceClick(source.source);
                                        }}
                                        title="Bearbeiten"
                                    >
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                        className="lead-source-badge-remove"
                                        onClick={(e) => handleRemoveSource(e, source.source)}
                                        title="Entfernen"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Organic Channels */}
            <div className="lead-source-category-section">
                <h3 className="lead-source-category-title">
                    <i className="fas fa-seedling"></i>
                    Organische Kanäle
                </h3>
                <div className="lead-source-grid">
                    {availableLeadSources.filter(s => s.category === 'organic').map(source => {
                        const isSelected = isSourceSelected(source.id);
                        return (
                            <div
                                key={source.id}
                                className={`lead-source-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleSourceClick(source.id)}
                            >
                                <i className={source.icon}></i>
                                <span>{source.label}</span>
                                {isSelected && (
                                    <div className="lead-source-card-strength">
                                        {getSourceStrength(source.id)}/10
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Paid Channels */}
            <div className="lead-source-category-section">
                <h3 className="lead-source-category-title">
                    <i className="fas fa-dollar-sign"></i>
                    Bezahlte Werbung
                </h3>
                <div className="lead-source-grid">
                    {availableLeadSources.filter(s => s.category === 'paid').map(source => {
                        const isSelected = isSourceSelected(source.id);
                        return (
                            <div
                                key={source.id}
                                className={`lead-source-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleSourceClick(source.id)}
                            >
                                <i className={source.icon}></i>
                                <span>{source.label}</span>
                                {isSelected && (
                                    <div className="lead-source-card-strength">
                                        {getSourceStrength(source.id)}/10
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Other Channels */}
            <div className="lead-source-category-section">
                <h3 className="lead-source-category-title">
                    <i className="fas fa-th"></i>
                    Weitere Kanäle
                </h3>
                <div className="lead-source-grid">
                    {availableLeadSources.filter(s => s.category === 'other').map(source => {
                        const isSelected = isSourceSelected(source.id);
                        return (
                            <div
                                key={source.id}
                                className={`lead-source-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleSourceClick(source.id)}
                            >
                                <i className={source.icon}></i>
                                <span>{source.label}</span>
                                {isSelected && (
                                    <div className="lead-source-card-strength">
                                        {getSourceStrength(source.id)}/10
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Quick Strength Modal */}
            {modalOpen && ReactDOM.createPortal(
                <div className="lead-source-modal-overlay" onClick={() => setModalOpen(false)}>
                    <div className="lead-source-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="lead-source-modal-header">
                            <h3>
                                <i className={availableLeadSources.find(s => s.id === currentSource)?.icon}></i>
                                {availableLeadSources.find(s => s.id === currentSource)?.label}
                            </h3>
                            <button className="lead-source-modal-close" onClick={() => setModalOpen(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="lead-source-modal-body">
                            <label>Traffic-Stärke dieser Quelle</label>
                            <p className="lead-source-modal-subtitle">Wie viel Traffic/Leads kommt hierüber?</p>
                            
                            <div className="lead-source-strength-slider">
                                <input
                                    type="range"
                                    min="1"
                                    max="10"
                                    value={tempStrength}
                                    onChange={(e) => setTempStrength(parseInt(e.target.value))}
                                    className="lead-source-slider"
                                />
                                <div className="lead-source-strength-display">
                                    <span className="lead-source-strength-value">{tempStrength}/10</span>
                                    <span className="lead-source-strength-label">{getStrengthLabel(tempStrength)}</span>
                                </div>
                            </div>

                            {/* Quick Strength Buttons */}
                            <div className="lead-source-quick-strength">
                                <button 
                                    className={`lead-source-quick-btn ${tempStrength <= 3 ? 'active' : ''}`}
                                    onClick={() => setTempStrength(2)}
                                >
                                    Wenig
                                </button>
                                <button 
                                    className={`lead-source-quick-btn ${tempStrength > 3 && tempStrength <= 6 ? 'active' : ''}`}
                                    onClick={() => setTempStrength(5)}
                                >
                                    Mittel
                                </button>
                                <button 
                                    className={`lead-source-quick-btn ${tempStrength > 6 && tempStrength <= 8 ? 'active' : ''}`}
                                    onClick={() => setTempStrength(7)}
                                >
                                    Stark
                                </button>
                                <button 
                                    className={`lead-source-quick-btn ${tempStrength > 8 ? 'active' : ''}`}
                                    onClick={() => setTempStrength(10)}
                                >
                                    Hauptkanal
                                </button>
                            </div>

                            {/* Additional Fields for Organic Platforms */}
                            {isOrganicPlatform(currentSource) && (
                                <>
                                    <div className="lead-source-divider"></div>
                                    
                                    {/* Posting Frequency */}
                                    <div className="lead-source-field">
                                        <label>
                                            <i className="fas fa-calendar-alt"></i>
                                            Posting-Frequenz
                                        </label>
                                        <p className="lead-source-modal-subtitle">Wie viele Posts pro Monat?</p>
                                        <div className="lead-source-strength-slider">
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={tempPostingFrequency}
                                                onChange={(e) => setTempPostingFrequency(parseInt(e.target.value))}
                                                className="lead-source-slider"
                                            />
                                            <div className="lead-source-strength-display">
                                                <span className="lead-source-strength-value">{tempPostingFrequency}</span>
                                                <span className="lead-source-strength-label">Posts/Monat</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content Quality */}
                                    <div className="lead-source-field">
                                        <label>
                                            <i className="fas fa-star"></i>
                                            Content-Qualität
                                        </label>
                                        <p className="lead-source-modal-subtitle">Produktionsaufwand pro Post</p>
                                        <div className="lead-source-strength-slider">
                                            <input
                                                type="range"
                                                min="1"
                                                max="10"
                                                value={tempContentQuality}
                                                onChange={(e) => setTempContentQuality(parseInt(e.target.value))}
                                                className="lead-source-slider"
                                            />
                                            <div className="lead-source-strength-display">
                                                <span className="lead-source-strength-value">{tempContentQuality}/10</span>
                                                <span className="lead-source-strength-label">
                                                    {tempContentQuality <= 3 ? 'Einfach' :
                                                     tempContentQuality <= 6 ? 'Standard' :
                                                     tempContentQuality <= 8 ? 'Hochwertig' : 'Premium'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Follower Count */}
                                    <div className="lead-source-field">
                                        <label>
                                            <i className="fas fa-users"></i>
                                            Follower-Anzahl
                                        </label>
                                        <p className="lead-source-modal-subtitle">Genaue Anzahl der Follower auf dieser Plattform</p>
                                        <input
                                            type="number"
                                            value={tempFollowerCount}
                                            onChange={(e) => setTempFollowerCount(e.target.value)}
                                            placeholder="z.B. 15000"
                                            className="lead-source-input"
                                            min="0"
                                        />
                                        <small className="lead-source-hint">
                                            Tragen Sie die aktuelle Follower-Zahl ein für eine präzise Berechnung
                                        </small>
                                    </div>
                                </>
                            )}

                            {/* Notes Field - Always visible */}
                            <div className="lead-source-divider"></div>
                            <div className="lead-source-field">
                                <label>
                                    <i className="fas fa-sticky-note"></i>
                                    Besonderheiten (Optional)
                                </label>
                                <p className="lead-source-modal-subtitle">Zusätzliche Informationen zu dieser Quelle</p>
                                <textarea
                                    className="lead-source-notes-textarea"
                                    placeholder="z.B. Besondere Zielgruppe, Content-Strategie, etc."
                                    value={tempNotes}
                                    onChange={(e) => setTempNotes(e.target.value)}
                                    rows="3"
                                />
                            </div>
                        </div>
                        <div className="lead-source-modal-footer">
                            <button className="lead-source-cancel-btn" onClick={() => setModalOpen(false)}>
                                Abbrechen
                            </button>
                            <button className="lead-source-save-btn" onClick={handleSaveStrength}>
                                <i className="fas fa-check"></i>
                                Speichern
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default LeadSourceSelector;

